import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface WebhookFormProps {
  serverErrors?: ServerErrors;
}

export const WebhookForm = ({serverErrors}: WebhookFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-12 mb-3">
          <InputField
            name="settings.postback_url"
            label="URL"
            required={true}
            tooltip="The URL you would like the alert details sent by HTTP POST."
            autoFocus={true}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <TextAreaField
            name="settings.headers"
            label="Custom HTTP Headers"
            required={false}
            maxLength={2048}
            tooltip='Optional custom HTTP headers in "Name: Value" format.'
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <SwitchField
            name="settings.use_legacy_payload"
            label="Legacy Payload"
            tooltip="Maintain compatibility with legacy handlers."
          />
        </div>
        <div className="col-6 mb-3"></div>
      </div>
      <SetupInfo>
        <div className="p-2">
          Use this integration to receive alert details to your own website or web application. You will need to setup a
          handler, accessible by a public URL which you enter into the URL field. We will then POST the alert data in
          JSON format to this URL.
        </div>
        <div className="p-2">
          <a
            target="_blank"
            href="https://support.uptime.com/hc/en-us/articles/115002560845-Configuring-Custom-Postback-URL-Webhooks"
          >
            View {IntegrationModuleInfo[IntegrationModules.WEBHOOK].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
